<template>
  <div class="co-page">
    <div><router-link to="/companies" class="co-back"><i class="fas fa-arrow-left"></i> Companies</router-link></div>

    <div v-if="notice" class="co-alert ok"><i class="fas fa-check-circle"></i> {{ notice }}</div>
    <div v-if="error" class="co-alert bad"><i class="fas fa-exclamation-triangle"></i> {{ error }}</div>

    <div v-if="loading" class="co-card co-empty">Loading…</div>
    <template v-else-if="c">
      <header class="co-hero">
        <div>
          <div class="zh-eyebrow"><span class="zh-dot"></span> Partner company</div>
          <h1 class="zh-title"><span>{{ c.name }}</span></h1>
          <div class="co-chips">
            <span v-if="c.archived" class="co-tag archived">Archived {{ niceDate(c.archivedAt) }}</span>
            <span v-if="c.belowMinimum" class="co-tag low">Low stock: below its minimum</span>
            <span class="co-tag on">{{ c.activeUsers }} of {{ c.users }} users active</span>
          </div>
        </div>
        <div class="co-actions">
          <template v-if="!c.archived">
            <button type="button" class="co-btn lg primary" @click="dialog = 'assign'"><i class="fas fa-plus-circle"></i> Assign trees</button>
            <button type="button" class="co-btn lg" @click="dialog = 'release'"><i class="fas fa-undo"></i> Release trees</button>
            <button type="button" class="co-btn lg danger" @click="dialog = 'archive'"><i class="fas fa-archive"></i> Archive</button>
          </template>
          <template v-else>
            <button type="button" class="co-btn lg primary" @click="dialog = 'restore'"><i class="fas fa-undo"></i> Restore</button>
            <button type="button" class="co-btn lg danger" @click="dialog = 'delete'"><i class="fas fa-trash-alt"></i> Delete for good</button>
          </template>
        </div>
      </header>

      <div class="co-tiles">
        <div class="co-tile"><span>Assigned trees</span><b>{{ fmt(c.assignedTrees) }}</b></div>
        <div class="co-tile"><span>Trees with stock</span><b>{{ fmt(c.treesWithStock) }}</b></div>
        <div class="co-tile" :class="{ low: c.belowMinimum }"><span>Splits available</span><b>{{ fmt(c.splitsAvailable) }}</b><small v-if="c.minAvailableSplits">minimum {{ fmt(c.minAvailableSplits) }}</small></div>
        <div class="co-tile"><span>Splits delivered</span><b>{{ fmt(c.splitsDelivered) }}</b></div>
      </div>

      <div class="cd-grid">
        <!-- users -->
        <section class="co-card cd-sec cd-users">
          <div class="cd-head">
            <h2><i class="fas fa-users"></i> Users</h2>
            <button v-if="!c.archived" type="button" class="co-btn primary" @click="inviting = true"><i class="fas fa-envelope-open-text"></i> Invite user</button>
          </div>
          <p class="cd-help">They sign in to the operations panel. An invitation is an email with a one-time link to choose their password, valid 7 days.</p>

          <div v-for="m in c.members" :key="m.id" class="cd-member">
            <span class="cd-avatar" :class="{ off: m.blocked }">{{ initials(m) }}</span>
            <div class="cd-who">
              <div class="cd-name">
                {{ m.firstName || m.email }}
                <span v-if="m.isCompanyOwner" class="co-tag owner">Owner</span>
                <span class="co-tag" :class="m.blocked ? 'off' : 'on'">{{ m.blocked ? 'Blocked' : 'Active' }}</span>
              </div>
              <div v-if="m.firstName" class="co-muted">{{ m.email }}</div>
              <div v-if="m.invitation" class="cd-inv" :class="m.invitation.status">
                <i class="fas" :class="m.invitation.status === 'pending' ? 'fa-paper-plane' : 'fa-hourglass-end'"></i>
                {{ m.invitation.status === 'pending' ? 'Invitation sent · valid until ' : 'Invitation expired on ' }}{{ niceDate(m.invitation.expiresAt) }}
              </div>
            </div>
            <div v-if="!c.archived" class="cd-member-actions">
              <button v-if="!m.blocked" type="button" class="co-btn" :disabled="busyUser !== null" @click="resend(m)">
                <i class="fas" :class="busyUser === m.id ? 'fa-circle-notch fa-spin' : 'fa-paper-plane'"></i> {{ m.invitation ? 'Resend invitation' : 'Send invitation' }}
              </button>
              <button v-if="m.invitation && m.invitation.status === 'pending'" type="button" class="co-btn" :disabled="busyUser !== null" @click="revoke(m)">Cancel invitation</button>
              <button type="button" class="co-btn" :class="{ danger: !m.blocked }" :disabled="busyUser !== null" @click="toggleUser(m)">{{ m.blocked ? 'Unblock' : 'Block' }}</button>
            </div>
          </div>
          <p v-if="!c.members || !c.members.length" class="co-empty">This company has no users yet. Invite the first one.</p>

          <div v-if="lastLink" class="cd-lastlink">
            <invitation-link :link="lastLink.link" :email="lastLink.email" :expires-at="lastLink.expiresAt"/>
            <button type="button" class="co-btn" @click="lastLink = null">Hide the link</button>
          </div>
        </section>

        <!-- company data -->
        <section class="co-card cd-sec">
          <div class="cd-head"><h2><i class="fas fa-building"></i> Company data</h2></div>
          <label class="cd-fld">
            <span>Name</span>
            <input v-model="form.name" type="text" maxlength="160" :disabled="c.archived">
          </label>
          <label class="cd-fld">
            <span>Wallet</span>
            <input v-model="form.wallet" type="text" class="mono" placeholder="0x…" :disabled="c.archived">
          </label>
          <label class="cd-fld">
            <span>Minimum splits available</span>
            <input v-model.number="form.minAvailableSplits" type="number" min="0" step="1" :disabled="c.archived">
            <small>When the splits available drop below this number the company shows in red, and administrators can be notified. 0 = no minimum.</small>
          </label>
          <button v-if="!c.archived" type="button" class="co-btn lg primary" :disabled="saving || !formDirty" @click="save">
            <i class="fas" :class="saving ? 'fa-circle-notch fa-spin' : 'fa-save'"></i> {{ saving ? 'Saving…' : 'Save changes' }}
          </button>
        </section>

        <!-- stock by forest -->
        <section class="co-card cd-sec">
          <div class="cd-head"><h2><i class="fas fa-tree"></i> Stock by forest</h2></div>
          <p class="cd-help">Where the company's trees come from.</p>
          <div class="table-responsive">
            <table class="co-table">
              <thead><tr><th>Forest</th><th class="num">Trees</th><th class="num">With stock</th><th class="num">Splits available</th><th class="num">Delivered</th></tr></thead>
              <tbody>
                <tr v-for="f in c.forests" :key="f.areaId">
                  <td class="strong">{{ f.forest }}</td>
                  <td class="num">{{ fmt(f.trees) }}</td>
                  <td class="num">{{ fmt(f.treesWithStock) }}</td>
                  <td class="num">{{ fmt(f.splitsAvailable) }}</td>
                  <td class="num">{{ fmt(f.splitsDelivered) }}</td>
                </tr>
                <tr v-if="!c.forests || !c.forests.length"><td colspan="5" class="co-empty">No trees assigned yet.</td></tr>
              </tbody>
            </table>
          </div>
        </section>

        <!-- history -->
        <section class="co-card cd-sec">
          <div class="cd-head"><h2><i class="fas fa-history"></i> Latest changes</h2></div>
          <div v-for="h in c.history" :key="h.id" class="cd-hist">
            <span class="cd-hist-icon" :class="h.action"><i :class="historyIcon(h.action)"></i></span>
            <div>
              <div class="cd-name">{{ actionLabel(h.action) }}<template v-if="h.action === 'assign' || h.action === 'release'"> {{ fmt(h.count) }} tree{{ h.count === 1 ? '' : 's' }}</template><span v-if="h.forest" class="co-muted"> · {{ h.forest }}</span></div>
              <div class="co-muted">{{ h.performedBy || '—' }} · {{ niceDateTime(h.at) }}</div>
            </div>
          </div>
          <p v-if="!c.history || !c.history.length" class="co-empty">Nothing yet.</p>
        </section>
      </div>
    </template>

    <template v-if="c">
      <trees-dialog v-if="dialog === 'assign' || dialog === 'release'" :mode="dialog" :company="c" @close="dialog = null" @done="finished"/>
      <company-action-dialog v-else-if="dialog" :mode="dialog" :company="c" @close="dialog = null" @done="finished"/>
      <invite-dialog v-if="inviting" :company="c" @close="inviting = false" @invited="invited"/>
    </template>
  </div>
</template>

<script>
import Companies, { apiError } from '../api/Companies';
import TreesDialog from '../components/Companies/TreesDialog.vue';
import CompanyActionDialog from '../components/Companies/CompanyActionDialog.vue';
import InviteDialog from '../components/Companies/InviteDialog.vue';
import InvitationLink from '../components/Companies/InvitationLink.vue';
import adminOnlyPage from '../mixins/adminOnlyPage';

/** One partner company: its users and their invitations, its data, its stock by forest and what was done with its trees. */
export default {
  name: 'CompanyDetail',
  components: { TreesDialog, CompanyActionDialog, InviteDialog, InvitationLink },
  mixins: [adminOnlyPage],
  data() {
    return {
      c: null, loading: true, error: '', notice: '', saving: false, busyUser: null, dialog: null, inviting: false,
      form: { name: '', wallet: '', minAvailableSplits: 0 }, lastLink: null,
    };
  },
  computed: {
    id() { return Number(this.$route.params.id); },
    formDirty() {
      if (!this.c) return false;
      return this.form.name !== this.c.name || (this.form.wallet || '') !== (this.c.wallet || '') || Number(this.form.minAvailableSplits) !== Number(this.c.minAvailableSplits);
    },
  },
  watch: {
    '$route.params.id'() { this.lastLink = null; this.notice = ''; this.load(true); },
  },
  created() { this.load(true); },
  methods: {
    fmt(v) { return new Intl.NumberFormat('en-GB').format(Number(v) || 0); },
    niceDate(d) { return d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : ''; },
    niceDateTime(d) { return d ? new Date(d).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'; },
    initials(m) { return String(m.firstName || m.email || '?').trim().split(/[\s@.]+/).filter(Boolean).slice(0, 2).map((s) => s[0].toUpperCase()).join(''); },
    actionLabel(a) { return { assign: 'Assigned', release: 'Released', archive: 'Archived', restore: 'Restored' }[a] || a; },
    historyIcon(a) { return { assign: 'fas fa-plus', release: 'fas fa-undo', archive: 'fas fa-archive', restore: 'fas fa-redo' }[a] || 'fas fa-circle'; },
    fillForm() { this.form = { name: this.c.name, wallet: this.c.wallet || '', minAvailableSplits: this.c.minAvailableSplits || 0 }; },
    async load(fillForm) {
      try {
        this.c = (await Companies.get(this.id)).data;
        if (fillForm) this.fillForm();
        this.error = '';
      } catch (err) {
        this.error = err && err.response && err.response.status === 404 ? 'This company does not exist.' : apiError(err, 'Could not load the company.');
      } finally {
        this.loading = false;
      }
    },
    async save() {
      if (this.saving || !this.c) return;
      this.saving = true;
      this.error = '';
      this.notice = '';
      try {
        const min = Number(this.form.minAvailableSplits);
        this.c = (await Companies.update(this.c.id, { name: this.form.name, wallet: this.form.wallet, minAvailableSplits: Number.isFinite(min) ? min : 0 })).data;
        this.fillForm();
        this.notice = 'Saved.';
      } catch (err) {
        this.error = apiError(err, 'Could not save.');
      } finally {
        this.saving = false;
      }
    },
    async toggleUser(m) {
      if (this.busyUser !== null) return;
      this.busyUser = m.id;
      this.error = '';
      this.notice = '';
      try {
        await Companies.setUserBlocked(this.c.id, m.id, !m.blocked);
        this.notice = `${m.firstName || m.email} is now ${m.blocked ? 'active' : 'blocked'}.`;
        await this.load(false);
      } catch (err) {
        this.error = apiError(err, 'Could not change the user.');
      } finally {
        this.busyUser = null;
      }
    },
    invited() {
      this.notice = '';
      this.load(false);
    },
    async resend(m) {
      if (this.busyUser !== null) return;
      this.busyUser = m.id;
      this.error = '';
      this.notice = '';
      try {
        const res = (await Companies.resendInvitation(m.id)).data || {};
        this.notice = `A new invitation was sent to ${m.email}. The previous link no longer works.`;
        this.lastLink = res.link ? { link: res.link, email: m.email, expiresAt: res.invitation && res.invitation.expiresAt } : null;
        await this.load(false);
      } catch (err) {
        this.error = apiError(err, 'Could not send the invitation.');
      } finally {
        this.busyUser = null;
      }
    },
    async revoke(m) {
      if (this.busyUser !== null) return;
      this.busyUser = m.id;
      this.error = '';
      this.notice = '';
      try {
        await Companies.revokeInvitation(m.id);
        this.notice = `The pending invitation of ${m.email} was cancelled.`;
        if (this.lastLink && this.lastLink.email === m.email) this.lastLink = null;
        await this.load(false);
      } catch (err) {
        this.error = apiError(err, 'Could not cancel the invitation.');
      } finally {
        this.busyUser = null;
      }
    },
    finished(message) {
      const kind = this.dialog;
      this.dialog = null;
      if (kind === 'delete') {
        this.$router.push('/companies');
        return;
      }
      this.notice = message;
      this.load(kind === 'restore' || kind === 'archive');
    },
  },
};
</script>

<style scoped lang="scss">
@import '../components/Stats/intro.scss';
@import '../components/Companies/companies.scss';

.co-back { font-weight: 700; font-size: 0.86rem; color: var(--accent-green); &:hover { text-decoration: none; } }
.co-chips { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 6px; .co-tag { margin-left: 0; } }
.co-actions { display: flex; flex-wrap: wrap; gap: 8px; }

.cd-grid { display: grid; grid-template-columns: minmax(0, 7fr) minmax(0, 5fr); gap: 16px; align-items: start; }
.cd-users { grid-row: span 2; }

.cd-sec { padding: 18px; }
.cd-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 6px;
  h2 { margin: 0; font-size: 1.05rem; font-weight: 800; color: var(--text-heading); i { color: var(--accent-green); margin-right: 6px; } }
  .co-btn { margin-left: 0; }
}
.cd-help { margin: 0 0 12px; font-size: 0.78rem; color: var(--text-muted); }

.cd-member {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  border-top: 1px solid var(--border-subtle);
  flex-wrap: wrap;
}
.cd-avatar {
  flex: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 0.86rem;
  color: #fff;
  background: var(--accent-gradient);
  &.off { background: #9aa59d; }
}
.cd-who { flex: 1; min-width: 180px; }
.cd-name { font-weight: 800; color: var(--text-heading); }
.cd-name .co-tag { margin-left: 4px; }
.cd-inv { margin-top: 2px; font-size: 0.76rem; color: var(--text-muted); i { margin-right: 4px; } &.expired { color: #c4532f; } }
.cd-member-actions { display: flex; flex-wrap: wrap; gap: 6px; .co-btn { margin-left: 0; } }
.cd-lastlink { margin-top: 12px; display: flex; flex-direction: column; gap: 8px; align-items: flex-start; .co-btn { margin-left: 0; } > :first-child { width: 100%; } }

.cd-fld {
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin: 0 0 12px;
  > span { font-size: 0.74rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: var(--text-muted); }
  input {
    height: 40px;
    padding: 0 12px;
    border-radius: 12px;
    border: 1px solid var(--border-color);
    background: var(--bg-secondary);
    color: var(--text-heading);
    font-weight: 600;
    outline: none;
    &:focus { border-color: var(--accent-green); box-shadow: 0 0 0 3px rgba(var(--accent-green-rgb), 0.15); }
    &.mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 0.82rem; }
    &:disabled { opacity: 0.7; }
  }
  small { font-size: 0.74rem; color: var(--text-muted); }
}

.cd-hist { display: flex; gap: 10px; padding: 9px 0; border-top: 1px solid var(--border-subtle); }
.cd-hist-icon {
  flex: none;
  width: 30px;
  height: 30px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.78rem;
  color: #fff;
  background: #9aa59d;
  &.assign { background: var(--accent-gradient); }
  &.release { background: #e0a52e; }
  &.archive { background: #e06a44; }
  &.restore { background: #159895; }
}

@media (max-width: 1199px) {
  .cd-grid { grid-template-columns: minmax(0, 1fr); }
  .cd-users { grid-row: auto; }
}
</style>
